import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { ChevronDown, ChevronRight, AlertTriangle, Check, Loader2, Minus, Plus, Archive, ExternalLink, Building2, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { getAllocationTargets, updateProcessedReceipt, commitProcessedReceipt } from '@/lib/api';
import { MasterAllocateDialog } from './MasterAllocateDialog';

// ─── Helpers ──────────────────────────────────────────────────
// Every allocation is a triplet { project_id, room_id, item_id }; we key
// them by `${projectId}:${roomId}:${itemId}` in a Set for O(1) toggles.
const keyOf = (a) => `${a.project_id}:${a.room_id}:${a.item_id}`;
const parseKey = (k) => {
  const [project_id, room_id, item_id] = k.split(':');
  return { project_id, room_id, item_id };
};

function statusForRow(allocated, qty) {
  if (allocated === 0) return 'empty';
  if (allocated > qty) return 'over';
  if (allocated === qty) return 'complete';
  return 'partial';
}

const STATUS_STYLE = {
  empty:    { pill: 'bg-[#5b6773]/10 text-[#5b6773]', ring: '' },
  partial:  { pill: 'bg-red-50 text-red-700 border border-red-200', ring: 'ring-1 ring-red-200' },
  complete: { pill: 'bg-emerald-50 text-emerald-700 border border-emerald-200', ring: '' },
  over:     { pill: 'bg-amber-50 text-amber-700 border border-amber-200', ring: 'ring-1 ring-amber-200' },
};

// ─── Top-level toggle pills for relevant projects ─────────────
function ProjectToggles({ projects, selectedIds, onToggle, onOnly, onSelectAll, onClear, onHideArchived }) {
  // Group: masters (with their subs), standalone projects, archived.
  const { masters, standalone, archived } = useMemo(() => {
    const m = [];
    const s = [];
    const a = [];
    const byParent = new Map();
    for (const p of projects) {
      if (p.archived) { a.push(p); continue; }
      if (p.is_master) { m.push(p); continue; }
      if (p.parent_id) {
        const arr = byParent.get(p.parent_id) || [];
        arr.push(p);
        byParent.set(p.parent_id, arr);
      } else {
        s.push(p);
      }
    }
    // Attach
    const mastersWithSubs = m.map(master => ({ ...master, _subs: byParent.get(master.id) || [] }));
    // Orphans (parent missing) → standalone
    for (const [pid, subs] of byParent) {
      if (!m.find(x => x.id === pid)) s.push(...subs);
    }
    return { masters: mastersWithSubs, standalone: s, archived: a };
  }, [projects]);

  const archivedCount = archived.length;

  return (
    <Card data-testid="project-toggles">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-wider text-[#5b6773] font-semibold">
            Relevant projects
          </p>
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={onSelectAll}
              className="text-[10px] px-2 py-1 rounded-full border border-[#2d948c]/25 text-[#2d948c] hover:bg-[#2d948c]/5 transition-colors"
              data-testid="project-toggles-select-all">All</button>
            <button type="button" onClick={onClear}
              className="text-[10px] px-2 py-1 rounded-full border border-[#5b6773]/25 text-[#5b6773] hover:bg-[#5b6773]/5 transition-colors"
              data-testid="project-toggles-clear">Clear</button>
            {archivedCount > 0 && (
              <button type="button" onClick={onHideArchived}
                className="text-[10px] px-2 py-1 rounded-full border border-amber-300 text-amber-700 hover:bg-amber-50 transition-colors inline-flex items-center gap-1"
                data-testid="project-toggles-hide-archived"
                title="Deselect all archived projects">
                <Archive className="h-3 w-3" /> Hide archived ({archivedCount})
              </button>
            )}
          </div>
        </div>

        <div className="space-y-3">
          {projects.length === 0 && (
            <p className="text-sm text-[#5b6773]">No projects yet — create one first.</p>
          )}

          {/* Master groups */}
          {masters.map(master => (
            <MasterToggleGroup
              key={master.id}
              master={master}
              subs={master._subs}
              selectedIds={selectedIds}
              onToggle={onToggle}
              onOnly={onOnly}
            />
          ))}

          {/* Standalone projects */}
          {standalone.length > 0 && (
            <div>
              {masters.length > 0 && (
                <p className="text-[9px] uppercase tracking-wider text-[#5b6773]/70 mb-1.5">Other projects</p>
              )}
              <div className="flex flex-wrap gap-2">
                {standalone.map(p => (
                  <ProjectPill key={p.id} project={p} selected={selectedIds.has(p.id)} onToggle={onToggle} onOnly={onOnly} />
                ))}
              </div>
            </div>
          )}

          {/* Archived */}
          {archived.length > 0 && (
            <div>
              <p className="text-[9px] uppercase tracking-wider text-amber-700/80 mb-1.5">Archived</p>
              <div className="flex flex-wrap gap-2">
                {archived.map(p => (
                  <ProjectPill key={p.id} project={p} selected={selectedIds.has(p.id)} onToggle={onToggle} onOnly={onOnly} archived />
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function MasterToggleGroup({ master, subs, selectedIds, onToggle, onOnly }) {
  const [expanded, setExpanded] = useState(true);
  const activeSubs = subs.filter(s => !s.archived);
  const selectedSubCount = activeSubs.filter(s => selectedIds.has(s.id)).length;
  const allSelected = activeSubs.length > 0 && selectedSubCount === activeSubs.length;
  const someSelected = selectedSubCount > 0 && !allSelected;

  const toggleAll = () => {
    // Toggle ALL its subs (and the master itself for consistency).
    const target = !allSelected;
    onToggle(master.id, target);
    for (const sub of activeSubs) onToggle(sub.id, target);
  };

  const onlyThisMaster = () => {
    // Select only this master + its active subs.
    onOnly(master.id, activeSubs.map(s => s.id));
  };

  return (
    <div className="rounded-lg border border-[#2d948c]/25 bg-gradient-to-br from-[#2d948c]/[0.04] to-transparent overflow-hidden">
      <div className="flex items-stretch">
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="px-2 hover:bg-[#2d948c]/10 transition-colors text-[#2d948c] flex items-center"
          data-testid={`master-toggle-group-${master.id}`}
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <button
          type="button"
          onClick={toggleAll}
          className={`flex-1 flex items-center gap-2 px-3 py-2 text-sm transition-colors text-left ${
            allSelected
              ? 'bg-[#2d948c] text-white'
              : someSelected
                ? 'bg-[#2d948c]/15 text-[#1f2933] hover:bg-[#2d948c]/20'
                : 'text-[#1f2933] hover:bg-[#2d948c]/10'
          }`}
          data-testid={`master-pill-${master.id}`}
        >
          <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="font-medium truncate">{master.name}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full tabular-nums ${
            allSelected ? 'bg-white/20' : someSelected ? 'bg-[#2d948c]/25 text-[#2d948c]' : 'bg-[#2d948c]/10 text-[#2d948c]'
          }`}>
            {selectedSubCount}/{activeSubs.length}
          </span>
        </button>
        <button
          type="button"
          onClick={onlyThisMaster}
          className={`px-2 text-[10px] font-semibold uppercase tracking-wider border-l transition-colors ${
            allSelected ? 'border-white/30 text-white/80 hover:bg-white/15' : 'border-[#2d948c]/20 text-[#5b6773] hover:bg-[#2d948c]/10 hover:text-[#2d948c]'
          }`}
          title="Only this master + its sub-units"
          data-testid={`master-only-${master.id}`}
        >
          Only
        </button>
      </div>

      {expanded && activeSubs.length > 0 && (
        <div className="px-3 py-2 border-t border-[#2d948c]/15 flex flex-wrap gap-1.5">
          {activeSubs.map(sub => (
            <ProjectPill
              key={sub.id}
              project={sub}
              selected={selectedIds.has(sub.id)}
              onToggle={onToggle}
              onOnly={onOnly}
              compact
            />
          ))}
        </div>
      )}
      {expanded && activeSubs.length === 0 && (
        <p className="px-3 py-2 text-[11px] text-[#5b6773] italic border-t border-[#2d948c]/15">
          No active sub-units in this master yet.
        </p>
      )}
    </div>
  );
}

function ProjectPill({ project, selected, onToggle, onOnly, archived, compact }) {
  return (
    <div
      className={`group relative inline-flex items-stretch rounded-full transition-all overflow-hidden ${compact ? 'text-xs' : 'text-sm'} ${
        selected
          ? 'bg-[#2d948c] text-white border border-[#2d948c]'
          : archived
            ? 'bg-amber-50/50 text-amber-800 border border-amber-200/70 hover:border-amber-300'
            : 'bg-white text-[#5b6773] border border-[#2d948c]/20 hover:border-[#2d948c]/40'
      }`}
      data-testid={`project-toggle-${project.id}`}
    >
      <button
        type="button"
        onClick={() => onToggle(project.id)}
        className={`inline-flex items-center gap-1 ${compact ? 'px-2.5 py-1' : 'px-3.5 py-1.5'}`}
        data-testid={`project-toggle-btn-${project.id}`}
      >
        {selected && <Check className="h-3 w-3 -mt-0.5" />}
        {archived && <Archive className="h-3 w-3 -mt-0.5 opacity-70" />}
        <span className="truncate max-w-[14rem]">{project.name}</span>
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onOnly(project.id); }}
        className={`px-2 text-[10px] font-semibold uppercase tracking-wider border-l transition-colors ${
          selected
            ? 'border-white/30 text-white/80 hover:bg-white/15'
            : 'border-current/20 hover:bg-[#2d948c]/10 hover:text-[#2d948c]'
        }`}
        title="Only this project"
        data-testid={`project-toggle-only-${project.id}`}
      >
        Only
      </button>
    </div>
  );
}

// ─── Per line-item allocation card ────────────────────────────
function LineItemAllocationCard({ line, projects, selectedProjectIds, allocations, onChangeCount, forceOpen, onForce, onCostMode, mastersWithSubs = [], onOpenMasterDialog }) {
  // `allocations` is a Map<`pid:rid:iid`, count> for THIS line.
  const qty = Math.max(0, Math.round(Number(line.quantity) || 0));
  // Total units allocated = SUM of counts, not number of distinct pills.
  const allocated = Array.from(allocations.values()).reduce((s, c) => s + (c || 0), 0);
  const status = statusForRow(allocated, qty);
  // Auto-collapse when complete unless the user manually re-opened.
  const isOpen = forceOpen ?? (status !== 'complete');
  const style = STATUS_STYLE[status];

  // Master projects themselves rarely hold rooms/items — they're aggregators.
  // Hide them from the per-line accordion (their sub-units render instead).
  const visibleProjects = projects.filter(p => selectedProjectIds.has(p.id) && !p.is_master);

  const [expandedProjects, setExpandedProjects] = useState(() => new Set());
  const [expandedRooms, setExpandedRooms] = useState(() => new Set());

  const toggleProject = (id) => setExpandedProjects(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleRoom = (id) => setExpandedRooms(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Per-project / per-room UNIT counts (sum of counts, not distinct pills).
  const projectCounts = useMemo(() => {
    const map = {};
    for (const [k, c] of allocations) {
      const a = parseKey(k);
      map[a.project_id] = (map[a.project_id] || 0) + c;
    }
    return map;
  }, [allocations]);

  const roomCounts = useMemo(() => {
    const map = {};
    for (const [k, c] of allocations) {
      const a = parseKey(k);
      const key = `${a.project_id}:${a.room_id}`;
      map[key] = (map[key] || 0) + c;
    }
    return map;
  }, [allocations]);

  return (
    <Card className={`${style.ring}`} data-testid={`line-card-${line.id}`}>
      <button
        onClick={() => onForce(line.id, !isOpen)}
        className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-[#2d948c]/[0.03] transition-colors"
        data-testid={`line-card-header-${line.id}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {isOpen ? <ChevronDown className="h-4 w-4 text-[#5b6773] flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-[#5b6773] flex-shrink-0" />}
          <div className="min-w-0">
            <p className="font-heading text-[15px] text-[#1f2933] truncate">{line.name}</p>
            <p className="text-xs text-[#5b6773]">
              Qty {qty} · Unit ${(Number(line.unit_price) || 0).toFixed(2)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {qty > 1 && (
            // Per-unit vs full-line-total toggle. Clicking this button should
            // NOT toggle the card's collapse state, so we stopPropagation.
            <div
              onClick={(e) => e.stopPropagation()}
              className="inline-flex text-[10px] rounded-full border border-[#2d948c]/25 p-0.5 bg-white"
              data-testid={`cost-mode-toggle-${line.id}`}
            >
              <button
                type="button"
                className={`px-2 py-0.5 rounded-full transition-colors ${line.cost_mode !== 'line_total' ? 'bg-[#2d948c] text-white' : 'text-[#5b6773]'}`}
                onClick={() => onCostMode(line.id, 'per_unit')}
                data-testid={`cost-mode-per-unit-${line.id}`}
              >
                Per unit
              </button>
              <button
                type="button"
                className={`px-2 py-0.5 rounded-full transition-colors ${line.cost_mode === 'line_total' ? 'bg-[#2d948c] text-white' : 'text-[#5b6773]'}`}
                onClick={() => onCostMode(line.id, 'line_total')}
                data-testid={`cost-mode-line-total-${line.id}`}
              >
                Line total
              </button>
            </div>
          )}
          {status === 'over' && <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />}
          <span className={`text-xs tabular-nums font-semibold px-2.5 py-1 rounded-full ${style.pill}`} data-testid={`line-status-${line.id}`}>
            {allocated} / {qty} allocated
          </span>
        </div>
      </button>

      {isOpen && (
        <CardContent className="pt-0 pb-4 px-4">
          {/* Master-level smart allocator: one row per master that has any
              sub-units currently selected at the top of the page. */}
          {mastersWithSubs.length > 0 && (
            <div className="mb-2 space-y-1.5">
              {mastersWithSubs.map(master => {
                const selectedSubs = master._subs.filter(s => selectedProjectIds.has(s.id));
                if (selectedSubs.length === 0) return null;
                return (
                  <button
                    key={master.id}
                    type="button"
                    onClick={() => onOpenMasterDialog(master, selectedSubs)}
                    className="w-full flex items-center justify-between gap-3 text-left rounded-lg border border-[#2d948c]/30 bg-gradient-to-r from-[#2d948c]/[0.06] to-transparent px-3 py-2 hover:from-[#2d948c]/[0.10] transition-colors"
                    data-testid={`master-allocate-btn-${line.id}-${master.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Layers className="h-4 w-4 text-[#2d948c] flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-[#1f2933] truncate">
                          Allocate one to each unit in <span className="text-[#2d948c]">{master.name}</span>
                        </p>
                        <p className="text-[10px] text-[#5b6773]">
                          {selectedSubs.length} sub-unit{selectedSubs.length === 1 ? '' : 's'} selected · auto-matches by item name
                        </p>
                      </div>
                    </div>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-[#2d948c] flex-shrink-0">
                      Open ›
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {visibleProjects.length === 0 && (
            <p className="text-xs text-[#5b6773] py-6 text-center">
              Select at least one project at the top of the page to allocate this line item.
            </p>
          )}
          <div className="space-y-2">
            {visibleProjects.map(proj => {
              const pOpen = expandedProjects.has(proj.id);
              const pCount = projectCounts[proj.id] || 0;
              return (
                <div key={proj.id} className="rounded-lg border border-[#2d948c]/15 overflow-hidden">
                  <button
                    onClick={() => toggleProject(proj.id)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-[#f3efe6]/40 hover:bg-[#f3efe6]/70 transition-colors"
                    data-testid={`line-${line.id}-project-${proj.id}`}
                  >
                    <div className="flex items-center gap-2 text-sm text-[#1f2933]">
                      {pOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      <span className="font-medium">{proj.name}</span>
                    </div>
                    {pCount > 0 && (
                      <span className="text-[10px] font-semibold bg-[#2d948c]/15 text-[#2d948c] px-2 py-0.5 rounded-full tabular-nums">
                        {pCount}
                      </span>
                    )}
                  </button>
                  {pOpen && (
                    <div className="divide-y divide-[#2d948c]/10">
                      {proj.rooms.length === 0 && (
                        <p className="text-xs text-[#5b6773] px-3 py-3">No rooms in this project.</p>
                      )}
                      {proj.rooms.map(room => {
                        const rKey = `${proj.id}:${room.id}`;
                        const rOpen = expandedRooms.has(rKey);
                        const rCount = roomCounts[rKey] || 0;
                        return (
                          <div key={room.id}>
                            <button
                              onClick={() => toggleRoom(rKey)}
                              className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#2d948c]/[0.04] transition-colors"
                              data-testid={`line-${line.id}-room-${room.id}`}
                            >
                              <div className="flex items-center gap-2 text-sm text-[#1f2933]">
                                {rOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                <span>{room.name}</span>
                              </div>
                              {rCount > 0 && (
                                <span className="text-[10px] font-semibold bg-[#2d948c]/15 text-[#2d948c] px-2 py-0.5 rounded-full tabular-nums">
                                  {rCount}
                                </span>
                              )}
                            </button>
                            {rOpen && (
                              <div className="px-3 pb-3 pt-1 flex flex-wrap gap-1.5">
                                {room.items.length === 0 && (
                                  <p className="text-xs text-[#5b6773]">No items in this room.</p>
                                )}
                                {room.items.map(it => {
                                  const k = keyOf({ project_id: proj.id, room_id: room.id, item_id: it.id });
                                  const count = allocations.get(k) || 0;
                                  return (
                                    <AllocationPill
                                      key={it.id}
                                      lineId={line.id}
                                      itemId={it.id}
                                      item={it}
                                      count={count}
                                      onChange={(delta) => onChangeCount(line.id, k, delta)}
                                    />
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// ─── Pill with inline +/- steppers once selected + hover preview ───────────────
function AllocationPill({ lineId, itemId, item, count, onChange }) {
  const selected = count > 0;
  const name = item?.name || '(unnamed)';
  const trigger = !selected ? (
    <button
      onClick={() => onChange(+1)}
      className="text-xs px-2.5 py-1 rounded-full transition-all bg-white text-[#5b6773] border border-[#2d948c]/20 hover:border-[#2d948c]/40"
      data-testid={`alloc-pill-${lineId}-${itemId}`}
    >
      {name}
    </button>
  ) : (
    <div
      className="inline-flex items-stretch text-xs rounded-full bg-[#2d948c] text-white border border-[#2d948c] overflow-hidden"
      data-testid={`alloc-pill-${lineId}-${itemId}`}
    >
      <span className="px-2.5 py-1 tabular-nums">
        {name} ×{count}
      </span>
      <button
        onClick={() => onChange(-1)}
        className="px-1.5 bg-white/10 hover:bg-white/20 transition-colors inline-flex items-center justify-center"
        title="Remove one"
        data-testid={`alloc-pill-minus-${lineId}-${itemId}`}
      >
        <Minus className="h-3 w-3" />
      </button>
      <button
        onClick={() => onChange(+1)}
        className="px-1.5 bg-white/10 hover:bg-white/25 transition-colors inline-flex items-center justify-center"
        title="Add one"
        data-testid={`alloc-pill-plus-${lineId}-${itemId}`}
      >
        <Plus className="h-3 w-3" />
      </button>
    </div>
  );

  const links = (item?.links || []).filter(Boolean);
  const hasDetails = !!(item && (links.length || item.specifications || item.notes ||
    item.budget != null || item.listed_price != null || item.ordered_price != null || item.status));
  if (!hasDetails) return trigger;

  const fmtMoney = (v) => (v == null || v === '') ? null : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  return (
    <HoverCard openDelay={200} closeDelay={80}>
      <HoverCardTrigger asChild>{trigger}</HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="start"
        className="w-80 p-3 text-xs space-y-2"
        data-testid={`alloc-pill-popover-${itemId}`}
      >
        <div className="flex items-start justify-between gap-2">
          <p className="font-heading text-sm text-[#1f2933] leading-tight">{name}</p>
          {item.status && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#f3efe6] text-[#5b6773] flex-shrink-0">
              {item.status}
            </span>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {item.budget != null && item.budget !== '' && (
            <div>
              <p className="text-[9px] uppercase tracking-wider text-[#5b6773]">Budget</p>
              <p className="tabular-nums font-medium">{fmtMoney(item.budget)}</p>
            </div>
          )}
          {item.listed_price != null && item.listed_price !== '' && (
            <div>
              <p className="text-[9px] uppercase tracking-wider text-[#5b6773]">Listed</p>
              <p className="tabular-nums font-medium">{fmtMoney(item.listed_price)}</p>
            </div>
          )}
          {item.ordered_price != null && item.ordered_price !== '' && (
            <div>
              <p className="text-[9px] uppercase tracking-wider text-[#5b6773]">Ordered</p>
              <p className="tabular-nums font-medium">{fmtMoney(item.ordered_price)}</p>
            </div>
          )}
        </div>
        {item.specifications && (
          <div>
            <p className="text-[9px] uppercase tracking-wider text-[#5b6773]">Specifications</p>
            <p className="whitespace-pre-wrap leading-snug">{item.specifications}</p>
          </div>
        )}
        {item.notes && (
          <div>
            <p className="text-[9px] uppercase tracking-wider text-[#5b6773]">Notes</p>
            <p className="whitespace-pre-wrap leading-snug">{item.notes}</p>
          </div>
        )}
        {links.length > 0 && (
          <div>
            <p className="text-[9px] uppercase tracking-wider text-[#5b6773] mb-1">Links</p>
            <ul className="space-y-1">
              {links.slice(0, 4).map((l, idx) => {
                const url = typeof l === 'string' ? l : (l?.url || '');
                const label = typeof l === 'string' ? l : (l?.label || l?.url || 'Link');
                if (!url) return null;
                return (
                  <li key={idx} className="truncate">
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[#2d948c] hover:underline inline-flex items-center gap-1"
                    >
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{label}</span>
                    </a>
                  </li>
                );
              })}
              {links.length > 4 && (
                <li className="text-[10px] text-[#5b6773]">+{links.length - 4} more</li>
              )}
            </ul>
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

// ─── Main view ────────────────────────────────────────────────
export function ReceiptAllocationView({ receipt, onBack, onSaved }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Allocation state: Map<line_id, Map<key, count>>. Seeded from receipt.items.
  const [alloc, setAlloc] = useState(() => {
    const map = new Map();
    for (const it of (receipt.items || [])) {
      const inner = new Map();
      for (const a of (it.allocations || [])) {
        // Back-compat: older docs stored allocations without `count`; treat
        // missing/zero as 1.
        const c = Math.max(1, Number(a.count) || 1);
        inner.set(keyOf(a), (inner.get(keyOf(a)) || 0) + c);
      }
      map.set(it.id, inner);
    }
    return map;
  });

  // Per-line cost_mode override (per_unit vs line_total). Mirrors
  // receipt.items[i].cost_mode and is sent back on save.
  const [costModes, setCostModes] = useState(() => {
    const out = {};
    for (const it of (receipt.items || [])) out[it.id] = it.cost_mode || 'per_unit';
    return out;
  });

  // Selected projects — drives which projects appear in each card's accordion.
  const [selectedProjects, setSelectedProjects] = useState(() => new Set());
  // Per-line user override of the auto-collapse behavior (line_id → boolean).
  const [forceOpen, setForceOpen] = useState(() => ({}));

  // Master allocation dialog: { line, master, selectedSubs } | null
  const [masterDialog, setMasterDialog] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await getAllocationTargets();
        const list = r.projects || [];
        setProjects(list);
        // Start with all projects selected (user deselects irrelevant ones,
        // per the spec).
        setSelectedProjects(new Set(list.map(p => p.id)));
      } catch (e) {
        toast.error('Failed to load projects');
      }
      setLoading(false);
    })();
  }, []);

  const toggleProjectPill = (id, value) => setSelectedProjects(prev => {
    const next = new Set(prev);
    const target = typeof value === 'boolean' ? value : !next.has(id);
    if (target) next.add(id); else next.delete(id);
    return next;
  });

  const onlyProject = (id, alsoInclude = []) => {
    setSelectedProjects(new Set([id, ...alsoInclude]));
  };
  const selectAllProjects = () => setSelectedProjects(new Set(projects.map(p => p.id)));
  const clearProjects = () => setSelectedProjects(new Set());
  const hideArchived = () => setSelectedProjects(prev => {
    const next = new Set(prev);
    for (const p of projects) {
      if (p.archived) next.delete(p.id);
    }
    return next;
  });

  const changeAllocationCount = (lineId, key, delta) => {
    setAlloc(prev => {
      const next = new Map(prev);
      const inner = new Map(next.get(lineId) || new Map());
      const current = inner.get(key) || 0;
      const updated = current + delta;
      if (updated <= 0) inner.delete(key);
      else inner.set(key, updated);
      next.set(lineId, inner);
      return next;
    });
    // Undo any manual force-open so the card can auto-collapse again
    // the moment the total lands exactly on qty.
    setForceOpen(prev => {
      if (!(lineId in prev)) return prev;
      const next = { ...prev };
      delete next[lineId];
      return next;
    });
  };

  const setLineCostMode = (lineId, mode) => {
    setCostModes(prev => ({ ...prev, [lineId]: mode }));
  };

  const setLineForceOpen = (lineId, open) => {
    setForceOpen(prev => ({ ...prev, [lineId]: open }));
  };

  // Index of masters → their (active) sub-projects (used for the master
  // allocate button shown per line).
  const mastersWithSubs = useMemo(() => {
    const masters = projects.filter(p => p.is_master && !p.archived);
    return masters.map(m => ({
      ...m,
      _subs: projects.filter(p => p.parent_id === m.id && !p.archived),
    })).filter(m => m._subs.length > 0);
  }, [projects]);

  const applyMasterAllocations = (lineId, ops) => {
    // ops = [{ project_id, room_id, item_id, count, sub_name }]
    setAlloc(prev => {
      const next = new Map(prev);
      const inner = new Map(next.get(lineId) || new Map());
      for (const op of ops) {
        const k = keyOf(op);
        inner.set(k, (inner.get(k) || 0) + (op.count || 1));
      }
      next.set(lineId, inner);
      return next;
    });
    // Clear any forced-collapse override on this line
    setForceOpen(prev => {
      if (!(lineId in prev)) return prev;
      const next = { ...prev };
      delete next[lineId];
      return next;
    });
    // The allocations target items that may have just been created; refresh
    // the projects list so those new items appear in the dropdowns and pills.
    getAllocationTargets().then(r => setProjects(r.projects || [])).catch(() => {});
  };

  const save = async () => {
    setSaving(true);
    try {
      // Build the items payload: expand the Map<key, count> back into a list
      // of {project_id, room_id, item_id, count} triplets. Also include the
      // per-line cost_mode so the backend persists the user's choice.
      const items = (receipt.items || []).map(it => {
        const inner = alloc.get(it.id) || new Map();
        const allocations = [];
        for (const [k, c] of inner) {
          const parts = parseKey(k);
          allocations.push({ ...parts, count: c });
        }
        return {
          ...it,
          allocations,
          cost_mode: costModes[it.id] || 'per_unit',
        };
      });
      const updated = await updateProcessedReceipt(receipt.id, { items });
      // Immediately commit — the user's Save click should finalise the push
      // into project items (ordered_price + status) and mirror a receipt
      // entry on each touched project.
      const committed = await commitProcessedReceipt(receipt.id);
      const s = committed.commit_summary || {};
      toast.success(
        s.projects_touched
          ? `Committed $${Math.round(s.total_committed || 0).toLocaleString('en-US')} across ${s.items_touched} item${s.items_touched === 1 ? '' : 's'} in ${s.projects_touched} project${s.projects_touched === 1 ? '' : 's'}`
          : 'Allocations saved',
      );
      onSaved?.(committed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save allocations');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-sm text-[#5b6773]">
        <Loader2 className="h-5 w-5 inline animate-spin mr-2" /> Loading projects…
      </div>
    );
  }

  // totalAllocated = sum of counts across all lines & pills.
  const totalAllocated = Array.from(alloc.values()).reduce(
    (s, inner) => s + Array.from(inner.values()).reduce((x, c) => x + c, 0),
    0,
  );
  const linesWithAny = Array.from(alloc.values()).filter(inner => Array.from(inner.values()).some(c => c > 0)).length;
  const totalQty = (receipt.items || []).reduce((s, it) => s + (Math.round(Number(it.quantity) || 0)), 0);

  return (
    <div className="space-y-4" data-testid="allocation-view">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#5b6773] font-semibold">Allocate line items to projects</p>
          <p className="text-sm text-[#1f2933]">
            {totalAllocated} of {totalQty} units allocated across {linesWithAny} line{linesWithAny === 1 ? '' : 's'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onBack} data-testid="allocation-back-btn">Back to review</Button>
          <Button onClick={save} disabled={saving} data-testid="allocation-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            {saving ? 'Saving…' : 'Save allocations'}
          </Button>
        </div>
      </div>

      <ProjectToggles
        projects={projects}
        selectedIds={selectedProjects}
        onToggle={toggleProjectPill}
        onOnly={onlyProject}
        onSelectAll={selectAllProjects}
        onClear={clearProjects}
        onHideArchived={hideArchived}
      />

      <div className="space-y-3">
        {(receipt.items || []).length === 0 && (
          <p className="text-sm text-[#5b6773] text-center py-10">
            No line items to allocate.
          </p>
        )}
        {(receipt.items || []).map(line => (
          <LineItemAllocationCard
            key={line.id}
            line={{ ...line, cost_mode: costModes[line.id] || 'per_unit' }}
            projects={projects}
            selectedProjectIds={selectedProjects}
            allocations={alloc.get(line.id) || new Map()}
            onChangeCount={changeAllocationCount}
            forceOpen={forceOpen[line.id]}
            onForce={setLineForceOpen}
            onCostMode={setLineCostMode}
            mastersWithSubs={mastersWithSubs}
            onOpenMasterDialog={(master, subs) => setMasterDialog({ line, master, selectedSubs: subs })}
          />
        ))}
      </div>

      {masterDialog && (
        <MasterAllocateDialog
          open={!!masterDialog}
          onOpenChange={(o) => { if (!o) setMasterDialog(null); }}
          line={masterDialog.line}
          master={masterDialog.master}
          selectedSubs={masterDialog.selectedSubs}
          onApply={(ops) => applyMasterAllocations(masterDialog.line.id, ops)}
        />
      )}
    </div>
  );
}
